import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { useReportUpload } from '@/features/reports/hooks/useReportUpload.tsx'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
// todo: evaluate this rule, because relying on the API models is fine
// eslint-disable-next-line no-restricted-imports
import { useCompany } from '@/contexts'
import {
  type ReportImportForm,
  ReportImportFormSchema
} from '@/features/reports/api/reports.data.ts'
import type { FileId } from '@/types/file.types'
import { ReportFormComponent } from '@/features/reports/components/report-form.tsx'
import { useMessageSnackbar } from '@/hooks/useMessageSnackbar'

export const Route = createFileRoute('/_authenticated/$companySlug/reports/import')({
  beforeLoad: ({ context }) => {
    context.auth.rejectIfMissingPermissions(['REPORTING_WRITE', 'REPORTING_READ'])
  },
  component: RouteComponent
})

function RouteComponent() {
  const { t } = useTranslation(['_routes', '_global'])
  const { id: companyId } = useCompany()
  const navigate = Route.useNavigate()
  const { showSuccess, showError } = useMessageSnackbar()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<ReportImportForm>({
    resolver: zodResolver(ReportImportFormSchema),
    defaultValues: {
      name: '',
      date: new Date(),
      files: [],
      siteIds: []
    }
  })

  const [uploadProgress, setUploadProgress] = useState<Record<FileId, number>>({})

  // Progress callback
  const handleProgress = (fileId: FileId, progress: number) => {
    setUploadProgress(prev => ({
      ...prev,
      [fileId]: progress
    }))
  }

  // Mutation for uploading a single file
  const uploadMutation = useReportUpload(handleProgress)

  // Submit handler: upload files in parallel with progress tracking
  const onSubmit = useCallback(
    async (data: ReportImportForm) => {

      try {
        // Upload all files in parallel
        await uploadMutation.mutateAsync({
          companyId,
          file: data.files.find(it => it instanceof File)!,
          formData: data,
          onProgress: handleProgress
        })

        showSuccess(
          t('routes.reports_import.main.messages.uploadSuccess')
        )
        navigate({
          to: '/$companySlug/reports'
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : 'unknown_error'
        const cause = error instanceof Error ? error.cause : 'An error as occured'
        showError(t(`_global:api.message.errors.${message}`, {
          defaultValue: cause as string
        }))
      }

    },
    [companyId, navigate, uploadMutation]
  )

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <PageHeader
        backArrow={{ historyBack: true }}
        subtitle={t('routes.reports_import.main.subtitle')}
      >
        {t('routes.reports_import.main.title')}
      </PageHeader>

      <ReportFormComponent
        control={control}
        errors={errors}
        isSubmitting={isSubmitting}
        mutation={uploadMutation}
        progress={uploadProgress}
        isEditMode={false}
      />
    </Box>
  )
}
